import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { observer } from 'mobx-react-lite'
import { getSnapshot, SnapshotOut } from 'mobx-state-tree'
import { ClientError } from 'graphql-request'
import { DS } from '../../../design-system'
import { useStore } from '../../../models'
import { SetUserRolesResultModel, UserModel, UserRoles } from '../../../models/api-store'
import { ConfirmDialogModal } from '../../ConfirmDialog/ConfirmDialogModal.web'

type ApiUser = Pick<SnapshotOut<typeof UserModel.Type>, '_id' | 'auth0Id' | 'email' | 'name' | 'userRoles'>
type RoleResult = SnapshotOut<typeof SetUserRolesResultModel.Type>

function errorMessage(error: unknown) {
  if (error instanceof ClientError)
    return error.response.errors?.map((entry) => entry.message).join('\n') || error.message
  return error instanceof Error ? error.message : 'Failed to update user roles'
}

function RoleUpdateResult({ result }: { result: RoleResult }) {
  const failed = result.auth0Status === 'failed' || result.mongoStatus === 'rolled_back'
  const mongoOnly = result.auth0Status === 'skipped_no_auth0_account'
  return (
    <View accessibilityLiveRegion="polite" style={[styles.notice, failed && styles.errorNotice]}>
      <DS.Text
        variant="Body Medium/Medium"
        color={failed ? 'Utility/Error' : mongoOnly ? 'Brand/Maroon' : 'Utility/Success'}
      >
        {failed ? 'Changes could not be saved' : mongoOnly ? 'Saved with a sync warning' : 'Roles updated'}
      </DS.Text>
      {failed ? (
        <DS.Text variant="Body Small/Regular" color="Utility/Error">
          {result.auth0Error || 'Auth0 could not be updated.'} Previous roles have been restored.
        </DS.Text>
      ) : (
        <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
          {mongoOnly
            ? 'No Auth0 account found. Saved in MongoDB only; roles must be synced after signup.'
            : 'New access takes effect after the user logs in again.'}
        </DS.Text>
      )}
      <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
        MongoDB: {result.mongoStatus?.replace('_', ' ')} · Auth0: {mongoOnly ? 'no account' : result.auth0Status}
      </DS.Text>
    </View>
  )
}

function RoleCard({
  title,
  description,
  selected,
  disabled,
  locked,
  onPress
}: {
  title: 'Admin' | 'Teacher'
  description: string
  selected: boolean
  disabled: boolean
  locked?: boolean
  onPress: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const keyboardProps = {
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key !== ' ') return
      event.preventDefault()
      if (disabled || event.repeat) return
      onPress()
    }
  }
  return (
    <Pressable
      {...keyboardProps}
      accessibilityRole="checkbox"
      accessibilityLabel={title}
      accessibilityHint={locked ? 'You cannot remove your own admin access.' : description}
      accessibilityState={{ checked: selected, disabled }}
      aria-checked={selected}
      disabled={disabled}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [
        styles.roleCard,
        selected && styles.selectedCard,
        hovered && !disabled && styles.hoveredCard,
        pressed && styles.pressedCard,
        disabled && !locked && styles.disabledCard
      ]}
    >
      <View style={styles.cardHeading}>
        <View style={styles.roleIcon}>
          <DS.Icon name={title === 'Admin' ? 'Shield' : 'Book'} size={24} color="Global/White" />
        </View>
        <View style={[styles.checkbox, selected && styles.checkedBox]}>
          {selected && <DS.Icon name="Check" size={14} color="Global/White" />}
        </View>
      </View>
      <DS.Text variant="Body Large/Medium">{title}</DS.Text>
      <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey" style={styles.description}>
        {description}
      </DS.Text>
      {locked && (
        <DS.Text variant="Caption/Medium" color="Brand/Maroon">
          Your admin role is locked.
        </DS.Text>
      )}
    </Pressable>
  )
}

export const RoleManagement = observer(({ id, disabled = false }: { id: string; disabled?: boolean }) => {
  const store = useStore()
  const [open, setOpen] = useState(false)
  const [user, setUser] = useState<ApiUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [admin, setAdmin] = useState(false)
  const [teacher, setTeacher] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RoleResult | null>(null)
  const savingRef = useRef(false)

  useEffect(() => {
    if (!open) return
    let active = true
    setLoading(true)
    setUser(null)
    setResult(null)
    setError(null)
    setConfirming(false)

    Promise.resolve(
      store.api.queryGetUserForRoleManagement({ id }, (qb) => qb._id.auth0Id.email.name.userRoles, {
        fetchPolicy: 'no-cache'
      })
    )
      .then((response) => {
        if (!active) return
        const record = response.getUserForRoleManagement
        const roles = record?.userRoles ?? []
        setUser(record ? getSnapshot(record) : null)
        setAdmin(roles.includes(UserRoles.admin))
        setTeacher(roles.includes(UserRoles.teacher))
      })
      .catch((error: unknown) => {
        if (active) setError(errorMessage(error))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [id, open, store.api])

  const roles = user?.userRoles ?? []
  const currentUser = store.session.user
  const isSelf =
    !!user &&
    !!currentUser &&
    (user._id === currentUser._id || (!!user.auth0Id && user.auth0Id === currentUser.auth0Id))
  const hasChanges = admin !== roles.includes(UserRoles.admin) || teacher !== roles.includes(UserRoles.teacher)
  const editingDisabled = saving || confirming
  const label = user?.name || user?.email || user?._id
  const changes = [
    ...(admin !== roles.includes(UserRoles.admin) ? [`${admin ? 'Add' : 'Remove'} Admin`] : []),
    ...(teacher !== roles.includes(UserRoles.teacher) ? [`${teacher ? 'Add' : 'Remove'} Teacher`] : [])
  ]

  function discardChanges() {
    setAdmin(roles.includes(UserRoles.admin))
    setTeacher(roles.includes(UserRoles.teacher))
    setError(null)
  }

  async function saveChanges() {
    if (!user || savingRef.current) return
    savingRef.current = true
    setConfirming(false)
    setSaving(true)
    setError(null)
    setResult(null)
    try {
      const response = await store.api.mutateSetUserRoles(
        { userId: user._id, admin, teacher },
        (qb) => qb.userId.auth0Id.previousRoles.roles.mongoStatus.auth0Status.auth0Error
      )
      const saved = response.setUserRoles
      if (!saved.roles) throw new Error('No role update result returned')
      const snapshot = getSnapshot(saved)
      setResult(snapshot)
      setUser({ ...user, userRoles: snapshot.roles, auth0Id: snapshot.auth0Id })
      setAdmin(saved.roles.includes(UserRoles.admin))
      setTeacher(saved.roles.includes(UserRoles.teacher))
    } catch (error) {
      setError(errorMessage(error))
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  return (
    <>
      <DS.Button
        accessibilityRole="button"
        type="On Dark"
        variant="Medium/Secondary"
        textColor="Brand/Maroon"
        style={styles.launchButton}
        leftIcon={<DS.Icon name="Lock-Locked" size={16} color="Brand/Maroon" />}
        disabled={disabled || open}
        onPress={() => {
          setLoading(true)
          setOpen(true)
        }}
      >
        Roles & access
      </DS.Button>
      <Modal
        visible={open}
        transparent
        animationType="fade"
        accessibilityLabel="Roles & access"
        onRequestClose={() => {
          if (!editingDisabled) setOpen(false)
        }}
      >
        <View style={styles.backdrop}>
          <View style={styles.dialog}>
            <ScrollView>
              <View style={styles.container}>
                <View style={styles.header}>
                  <View style={styles.headerCopy}>
                    <DS.Text variant="Heading 3/Medium">Roles & access</DS.Text>
                    {user && (
                      <DS.Text variant="Body Small/Medium" color="Brand/Maroon">
                        {label}
                      </DS.Text>
                    )}
                  </View>
                  <DS.Button
                    accessibilityRole="button"
                    accessibilityLabel="Close role settings"
                    type="On Dark"
                    variant="Small/Secondary"
                    disabled={editingDisabled}
                    onPress={() => setOpen(false)}
                    leftIcon={<DS.Icon name="Close" size={12} />}
                  >
                    Close
                  </DS.Button>
                </View>
                {loading ? (
                  <DS.Text variant="Body Medium/Regular">Loading...</DS.Text>
                ) : user ? (
                  <>
                    <View style={styles.currentRoles}>
                      <DS.Text variant="Caption/Medium" color="Brand/Dark Grey">
                        CURRENT ROLES
                      </DS.Text>
                      {roles.length > 0 ? (
                        roles.map((role) => (
                          <View key={role} style={styles.currentRoleBadge}>
                            <DS.Text variant="Caption/Medium" color="Brand/Maroon" style={styles.roleName}>
                              {role}
                            </DS.Text>
                          </View>
                        ))
                      ) : (
                        <DS.Text variant="Body Small/Regular">No stored roles</DS.Text>
                      )}
                    </View>
                    <View style={styles.cards}>
                      <RoleCard
                        title="Admin"
                        description="Manage users and roles."
                        selected={admin}
                        disabled={isSelf || editingDisabled}
                        locked={isSelf}
                        onPress={() => setAdmin(!admin)}
                      />
                      <RoleCard
                        title="Teacher"
                        description="Access teaching tools."
                        selected={teacher}
                        disabled={editingDisabled}
                        onPress={() => setTeacher(!teacher)}
                      />
                    </View>
                    <View style={styles.footer}>
                      <View style={styles.footerCopy} accessibilityLiveRegion="polite">
                        {hasChanges && <DS.Text variant="Body Small/Medium">{changes.join(' · ')}</DS.Text>}
                        <View style={styles.baseRole}>
                          <DS.Icon name="Lock-Locked" size={14} color="Brand/Dark Grey" />
                          <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
                            User access always included.
                          </DS.Text>
                        </View>
                      </View>
                      <View style={styles.actions}>
                        {hasChanges && (
                          <DS.Button
                            accessibilityRole="button"
                            type="On Dark"
                            variant="Medium/Secondary"
                            disabled={editingDisabled}
                            onPress={discardChanges}
                          >
                            Discard changes
                          </DS.Button>
                        )}
                        <DS.Button
                          accessibilityRole="button"
                          type="On Light"
                          variant="Medium/Primary"
                          backgroundColor="Brand/Maroon"
                          disabled={!hasChanges || editingDisabled}
                          onPress={() => setConfirming(true)}
                        >
                          {saving ? 'Saving...' : 'Review changes'}
                        </DS.Button>
                      </View>
                    </View>
                    <ConfirmDialogModal
                      visible={confirming}
                      title="Confirm role changes"
                      message={`${label}\n\n${changes.join('\n')}`}
                      cancelText="Keep editing"
                      confirmText="Save changes"
                      onCancel={() => setConfirming(false)}
                      onConfirm={saveChanges}
                    />
                  </>
                ) : !error ? (
                  <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
                    No API user record found
                  </DS.Text>
                ) : null}
                {error && (
                  <View accessibilityRole="alert" style={[styles.notice, styles.errorNotice]}>
                    <DS.Text variant="Body Small/Medium" color="Utility/Error">
                      {error}
                    </DS.Text>
                  </View>
                )}
                {result && <RoleUpdateResult result={result} />}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  )
})

const styles = StyleSheet.create({
  launchButton: { marginLeft: 'auto' },
  backdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.45)'
  },
  dialog: {
    width: '100%',
    maxWidth: 800,
    maxHeight: '90%',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: DS.palette['Global/White']
  },
  container: {
    padding: 24,
    gap: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    backgroundColor: DS.palette['Global/White']
  },
  header: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16 },
  headerCopy: { flex: 1, minWidth: 220, gap: 8 },
  currentRoles: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
  currentRoleBadge: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: DS.palette['Admin/Badge']
  },
  roleName: { textTransform: 'capitalize' },
  cards: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  roleCard: {
    flexBasis: 240,
    flexGrow: 1,
    padding: 20,
    gap: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    backgroundColor: DS.palette['Global/White']
  },
  selectedCard: { borderColor: DS.palette['Brand/Maroon'], backgroundColor: DS.palette['Admin/Badge'] },
  hoveredCard: { borderColor: DS.palette['Brand/Maroon'] },
  pressedCard: { opacity: 0.85 },
  disabledCard: { opacity: 0.6 },
  cardHeading: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  roleIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: DS.palette['Brand/Maroon'],
    alignItems: 'center',
    justifyContent: 'center'
  },
  checkbox: {
    width: 22,
    height: 22,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Dark Grey'],
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center'
  },
  checkedBox: { borderColor: DS.palette['Brand/Maroon'], backgroundColor: DS.palette['Brand/Maroon'] },
  description: { flexGrow: 1, lineHeight: 21 },
  baseRole: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  footer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    borderTopWidth: 1,
    borderTopColor: DS.palette['Brand/Light Grey'],
    paddingTop: 20
  },
  footerCopy: { flexGrow: 1, flexBasis: 200, gap: 6 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', maxWidth: '100%', gap: 10 },
  notice: {
    padding: 16,
    borderRadius: 10,
    backgroundColor: DS.palette['Admin/Background'],
    gap: 8
  },
  errorNotice: { borderWidth: 1, borderColor: DS.palette['Utility/Error'] }
})
